const DEFAULT_STUDIO_DISCORD_INVITE_URL = "https://discord.gg/sUseKH8Hxb";

export type OnlineDemoLandingOptions = {
  discordInviteUrl?: string;
};

export type OnlineDemoLanding = {
  dispose: () => void;
};

type LandingLanguage = "es" | "en";

const LANDING_LANGUAGE_STORAGE_KEY = "pivotArena.landingLanguage";

const LANDING_COPY = {
  es: {
    htmlLang: "es",
    pageTitle: "PivotArena Game Studio | Argentina",
    metaDescription: "PivotArena Game Studio: desarrolladora de videojuegos en Argentina.",
    navStudio: "Estudio",
    navGames: "Juegos",
    languageToggleLabel: "Cambiar a inglés",
    languageToggleText: "EN",
    heroLabel: "Estudio argentino de videojuegos",
    heroCopy: "Desarrolladora de videojuegos en Argentina.",
    ctaGames: "Ver nuestros juegos",
    joinDiscord: "Unirse a Discord",
    gamesTitle: "Nuestro proyecto",
    projectStatus: "En desarrollo",
    projectDescription: "Diseñá tanques modulares, construí defensas y disputá el frente.",
    studioLabel: "El estudio",
    studioDescription: "Desarrolladora de videojuegos en Argentina.",
    locationTitle: "Ubicación",
    locationValue: "Argentina",
    activityTitle: "Actividad",
    activityValue: "Desarrollo de videojuegos",
    argentinaBadge: "Estudio argentino",
    footerStudio: "Estudio",
    footerGames: "Juegos",
  },
  en: {
    htmlLang: "en",
    pageTitle: "PivotArena Game Studio | Argentina",
    metaDescription: "PivotArena Game Studio: an Argentine video game developer.",
    navStudio: "Studio",
    navGames: "Games",
    languageToggleLabel: "Switch to Spanish",
    languageToggleText: "ES",
    heroLabel: "Argentine game studio",
    heroCopy: "Video game development from Argentina.",
    ctaGames: "Explore our games",
    joinDiscord: "Join Discord",
    gamesTitle: "Our project",
    projectStatus: "In development",
    projectDescription: "Design modular tanks, build defenses, and fight for the front.",
    studioLabel: "The studio",
    studioDescription: "An Argentine video game developer.",
    locationTitle: "Location",
    locationValue: "Argentina",
    activityTitle: "Focus",
    activityValue: "Video game development",
    argentinaBadge: "Argentine game studio",
    footerStudio: "Studio",
    footerGames: "Games",
  },
} as const;

let activeLanding: OnlineDemoLanding | null = null;

const landingCss = `
html.online-demo-page,
body.online-demo-page {
  margin: 0;
  min-width: 320px;
  min-height: 100%;
  height: auto;
  overflow-x: hidden;
  overflow-y: auto;
  color: #171717;
  background: #fff;
}

body.online-demo-page {
  font-family:
    Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI",
    sans-serif;
}

body.online-demo-page #app {
  height: auto;
  min-height: 100vh;
  background: #fff;
}

#online-demo-landing {
  min-height: 100vh;
  overflow: hidden;
  color: #181818;
  background:
    linear-gradient(90deg, rgba(0, 0, 0, 0.035) 1px, transparent 1px),
    linear-gradient(0deg, rgba(0, 0, 0, 0.035) 1px, transparent 1px),
    #fff;
  background-size: 34px 34px;
}

#online-demo-landing * {
  box-sizing: border-box;
}

.online-demo-nav {
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding: 16px clamp(18px, 4vw, 56px);
  border-bottom: 3px solid #171717;
  background: rgba(255, 255, 255, 0.94);
  backdrop-filter: blur(14px);
}

.online-demo-mark {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  position: relative;
  font-family: Impact, Haettenschweiler, "Arial Black", sans-serif;
  font-size: clamp(22px, 4vw, 36px);
  line-height: 0.92;
  letter-spacing: 0;
  text-transform: uppercase;
  color: #e31b23;
  -webkit-text-stroke: 1.5px #050505;
  text-shadow: 3px 3px 0 #050505;
  transform: rotate(-2deg) skew(-4deg);
}

.online-demo-navlinks {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.online-demo-link,
.online-demo-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  padding: 11px 16px;
  border: 2px solid #171717;
  border-radius: 6px;
  color: #171717;
  background: #fff;
  box-shadow: 4px 4px 0 #171717;
  font-weight: 900;
  line-height: 1;
  text-decoration: none;
  text-transform: uppercase;
  transition:
    transform 140ms ease,
    box-shadow 140ms ease;
}

.online-demo-link:hover,
.online-demo-button:hover {
  transform: translate(2px, 2px);
  box-shadow: 2px 2px 0 #171717;
}

.online-demo-button.primary {
  background: #e31b23;
  color: #fff;
}

.online-demo-button.secondary {
  background: #f8d84a;
}

.online-demo-language-toggle {
  cursor: pointer;
}

.online-demo-donate-top {
  background: #f8d84a;
  color: #171717;
  transform: rotate(1deg);
}

.online-demo-donate-top::before {
  content: "$";
  display: inline-grid;
  place-items: center;
  width: 20px;
  height: 20px;
  margin-right: 8px;
  border: 2px solid #171717;
  border-radius: 999px;
  background: #fff;
  font-size: 13px;
  line-height: 1;
}

.online-demo-button.discord,
.online-demo-link.discord {
  background: #5865f2;
  color: #fff;
}

.online-demo-hero {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: clamp(28px, 5vw, 58px);
  width: min(1180px, calc(100% - 36px));
  margin: 0 auto;
  padding: clamp(42px, 7vw, 78px) 0 clamp(30px, 6vw, 68px);
}

.online-demo-label {
  display: inline-flex;
  width: fit-content;
  margin-bottom: 18px;
  padding: 8px 12px;
  border: 2px solid #171717;
  border-radius: 999px;
  background: #e31b23;
  color: #fff;
  font-size: 13px;
  font-weight: 950;
  text-transform: uppercase;
}

.online-demo-title {
  position: relative;
  display: inline-block;
  margin: 0;
  padding: clamp(6px, 1vw, 12px) clamp(18px, 3vw, 36px) clamp(8px, 1.2vw, 16px);
  font-family: Impact, Haettenschweiler, "Arial Black", sans-serif;
  font-size: clamp(62px, 11vw, 138px);
  line-height: 0.92;
  letter-spacing: 0;
  text-transform: uppercase;
  white-space: nowrap;
  color: #e31b23;
  -webkit-text-stroke: clamp(2.5px, 0.38vw, 5px) #050505;
  transform: rotate(-2deg) skew(-4deg);
  text-shadow:
    0 clamp(5px, 0.65vw, 9px) 0 #050505,
    0 clamp(12px, 1.35vw, 18px) clamp(18px, 2vw, 28px) rgba(0, 0, 0, 0.62),
    clamp(3px, 0.45vw, 6px) clamp(1px, 0.18vw, 3px) 0 #050505,
    clamp(-6px, -0.45vw, -3px) clamp(-2px, -0.12vw, -1px) 0 #050505;
}

.online-demo-title::before {
  content: "";
  position: absolute;
  left: clamp(-24px, -2vw, -16px);
  right: clamp(-22px, -1.6vw, -14px);
  top: clamp(8px, 1vw, 16px);
  bottom: clamp(4px, 0.6vw, 9px);
  z-index: -1;
  background: rgba(5, 5, 5, 0.75);
  clip-path: polygon(0 35%, 7% 6%, 20% 18%, 31% 0, 45% 12%, 58% 3%, 70% 16%, 84% 2%, 100% 26%, 94% 95%, 77% 86%, 62% 100%, 47% 88%, 32% 97%, 18% 84%, 6% 98%);
  transform: rotate(1.5deg);
}

.online-demo-title::after {
  content: "";
  position: absolute;
  left: clamp(18px, 2vw, 28px);
  right: clamp(18px, 2vw, 32px);
  bottom: clamp(-9px, -0.8vw, -5px);
  height: clamp(7px, 0.75vw, 11px);
  background: currentColor;
  opacity: 0.75;
  transform: skew(-24deg) rotate(-1deg);
  box-shadow: clamp(8px, 1vw, 13px) clamp(7px, 0.9vw, 12px) 0 rgba(5, 5, 5, 0.65);
}

.online-demo-copy {
  max-width: 620px;
  margin: clamp(22px, 3vw, 34px) 0 0;
  font-family: Impact, Haettenschweiler, "Arial Black", sans-serif;
  font-size: clamp(25px, 4.4vw, 52px);
  line-height: 0.98;
  letter-spacing: 0;
  text-transform: uppercase;
  font-weight: 900;
}

.online-demo-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 28px;
}

.online-demo-warning {
  margin: 18px 0 0;
  max-width: 620px;
  color: #4f2600;
  font-size: 14px;
  font-weight: 850;
  line-height: 1.45;
}

.online-demo-mobile-warning {
  display: none;
  max-width: 620px;
  margin: 18px 0 0;
  padding: 12px 14px;
  border: 3px solid #171717;
  border-radius: 8px;
  background: #f8d84a;
  color: #171717;
  box-shadow: 5px 5px 0 #171717;
  font-size: 14px;
  font-weight: 900;
  line-height: 1.35;
}

#online-demo-landing[data-mobile-warning="true"] .online-demo-mobile-warning {
  display: block;
}

.online-demo-status {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 12px;
  align-items: center;
  max-width: 480px;
  margin-top: 24px;
  padding: 16px;
  border: 3px solid #171717;
  border-radius: 8px;
  background: #fff;
  box-shadow: 6px 6px 0 #171717;
}

.online-demo-status-light {
  width: 18px;
  height: 18px;
  border: 2px solid #171717;
  border-radius: 999px;
  background: #9ca3af;
}

#online-demo-landing[data-server-state="online"] .online-demo-status-light {
  background: #e31b23;
}

#online-demo-landing[data-server-state="full"] .online-demo-status-light {
  background: #f8d84a;
}

#online-demo-landing[data-server-state="offline"] .online-demo-status-light {
  background: #ff4d2e;
}

.online-demo-status strong {
  display: block;
  font-size: 17px;
  line-height: 1.15;
  text-transform: uppercase;
}

.online-demo-status span {
  display: block;
  margin-top: 4px;
  color: #4a4a4a;
  font-size: 14px;
  font-weight: 700;
}

.online-demo-visual {
  position: relative;
  width: 100%;
  min-width: 0;
  aspect-ratio: 16 / 9;
  min-height: clamp(220px, 34vw, 430px);
  overflow: hidden;
  border: 4px solid #171717;
  border-radius: 8px;
  background:
    linear-gradient(135deg, rgba(227, 27, 35, 0.07), rgba(255, 255, 255, 0.95)),
    linear-gradient(90deg, rgba(5, 5, 5, 0.08) 1px, transparent 1px),
    linear-gradient(0deg, rgba(5, 5, 5, 0.08) 1px, transparent 1px),
    #fff;
  background-size: auto, 28px 28px, 28px 28px;
  transform: rotate(-3deg);
  box-shadow: 14px 14px 0 #171717;
}

.online-demo-trailer {
  display: block;
  width: 100%;
  height: 100%;
  min-height: inherit;
  object-fit: cover;
  background: #050505;
}

.online-demo-visual::after {
  content: "";
  position: absolute;
  inset: 0;
  pointer-events: none;
  border: 3px solid rgba(255, 255, 255, 0.9);
  mix-blend-mode: screen;
}

.online-demo-section {
  width: min(1180px, calc(100% - 36px));
  margin: 0 auto;
  padding: clamp(34px, 6vw, 76px) 0;
}

.online-demo-section h2 {
  margin: 0 0 20px;
  font-family: Impact, Haettenschweiler, "Arial Black", sans-serif;
  font-size: clamp(34px, 6vw, 76px);
  line-height: 0.92;
  letter-spacing: 0;
  text-transform: uppercase;
}

.online-demo-features {
  display: grid;
  grid-template-columns: 1fr;
  gap: 18px;
}

.online-demo-feature {
  display: grid;
  grid-template-columns: minmax(220px, 0.9fr) minmax(0, 1fr);
  gap: 18px;
  align-items: center;
  min-height: 250px;
  padding: 18px;
  border: 3px solid #171717;
  border-radius: 8px;
  background: #fff;
  box-shadow: 7px 7px 0 #171717;
}

.online-demo-feature:nth-child(2) {
  background: #fff3f3;
}

.online-demo-feature:nth-child(4) {
  background: #fff3f3;
}

.online-demo-media-slot {
  width: 100%;
  aspect-ratio: 16 / 9;
  min-height: 160px;
  border: 3px dashed rgba(23, 23, 23, 0.74);
  border-radius: 6px;
  background:
    linear-gradient(135deg, rgba(227, 27, 35, 0.08), rgba(255, 255, 255, 0.92)),
    linear-gradient(90deg, rgba(5, 5, 5, 0.07) 1px, transparent 1px),
    linear-gradient(0deg, rgba(5, 5, 5, 0.07) 1px, transparent 1px),
    #fff;
  background-size: auto, 24px 24px, 24px 24px;
}

.online-demo-feature h3 {
  margin: 0;
  font-size: clamp(22px, 3vw, 32px);
  line-height: 1;
  text-transform: uppercase;
}

.online-demo-feature p {
  margin: 12px 0 0;
  color: #303030;
  font-weight: 700;
  line-height: 1.45;
}

.online-demo-support {
  display: grid;
  grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr);
  gap: 20px;
  align-items: center;
  border-top: 4px solid #171717;
  border-bottom: 4px solid #171717;
}

.online-demo-support-card {
  display: grid;
  gap: 12px;
  padding: 18px;
  border: 3px solid #171717;
  border-radius: 8px;
  background: #f8d84a;
  box-shadow: 7px 7px 0 #171717;
}

.online-demo-support-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 12px;
  align-items: center;
  padding: 14px;
  border: 2px solid #171717;
  border-radius: 6px;
  background: #fff;
}

.online-demo-support-row strong,
.online-demo-support-row span {
  display: block;
}

.online-demo-support-row strong {
  text-transform: uppercase;
}

.online-demo-support-row span {
  margin-top: 4px;
  color: #303030;
  font-weight: 750;
}

.online-demo-copy-alias {
  cursor: pointer;
  min-width: 132px;
}

.online-demo-footer {
  padding: clamp(38px, 7vw, 78px) clamp(18px, 4vw, 56px);
  border-top: 3px solid #171717;
  background: #171717;
  color: #fff;
}

.online-demo-footer-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  width: min(1180px, 100%);
  margin: 0 auto;
}

.online-demo-footer h2 {
  margin: 0;
  font-family: Impact, Haettenschweiler, "Arial Black", sans-serif;
  font-size: clamp(34px, 6vw, 76px);
  line-height: 0.9;
  letter-spacing: 0;
  text-transform: uppercase;
}

.online-demo-footer .online-demo-link {
  color: #171717;
}

.online-demo-footer-bottom {
  display: flex;
  justify-content: flex-end;
  width: min(1180px, 100%);
  margin: 24px auto 0;
}

.online-demo-argentina-badge {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: rgba(255, 255, 255, 0.88);
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0;
  text-transform: uppercase;
}

.online-demo-argentina-flag {
  position: relative;
  display: inline-block;
  width: 34px;
  height: 21px;
  border: 2px solid rgba(255, 255, 255, 0.88);
  border-radius: 3px;
  background: linear-gradient(to bottom, #6cb6e8 0 33.33%, #fff 33.33% 66.66%, #6cb6e8 66.66% 100%);
  box-shadow: 2px 2px 0 rgba(0, 0, 0, 0.35);
}

.online-demo-argentina-flag::after {
  content: "";
  position: absolute;
  left: 50%;
  top: 50%;
  width: 5px;
  height: 5px;
  border-radius: 999px;
  background: #f8d84a;
  transform: translate(-50%, -50%);
}

@media (max-width: 860px) {
  .online-demo-nav,
  .online-demo-footer-inner {
    align-items: stretch;
    flex-direction: column;
  }

  .online-demo-navlinks,
  .online-demo-actions {
    display: grid;
    grid-template-columns: 1fr;
    width: 100%;
  }

  .online-demo-link,
  .online-demo-button {
    width: 100%;
  }

  .online-demo-hero,
  .online-demo-support {
    grid-template-columns: minmax(0, 1fr);
  }

  .online-demo-features {
    grid-template-columns: 1fr;
  }

  .online-demo-feature {
    grid-template-columns: 1fr;
  }

  .online-demo-title {
    white-space: normal;
  }

  .online-demo-visual {
    transform: rotate(-1deg);
    box-shadow: 8px 8px 0 #171717;
  }

  .online-demo-support-row {
    grid-template-columns: 1fr;
  }

  .online-demo-footer-bottom {
    justify-content: flex-start;
  }
}


.studio-feature-logo {
  display: block;
  width: 100%;
  height: 100%;
  padding: clamp(18px, 4vw, 42px);
  object-fit: contain;
}

.studio-card-logo {
  display: block;
  width: 100%;
  height: 100%;
  min-height: 160px;
  padding: 12px;
  object-fit: contain;
}`;

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function normalizeSafeHref(value: string | undefined, fallback: string): string {
  const candidate = value?.trim() || fallback;
  try {
    const url = new URL(candidate);
    if (url.protocol === "http:" || url.protocol === "https:") return url.href;
  } catch {}
  return fallback;
}

function isLandingLanguage(value: string | null | undefined): value is LandingLanguage {
  return value === "es" || value === "en";
}

function resolveLandingLanguage(): LandingLanguage {
  try {
    const urlLanguage = new URLSearchParams(window.location.search).get("lang");
    if (isLandingLanguage(urlLanguage)) return urlLanguage;
  } catch {}

  try {
    const storedLanguage = window.localStorage?.getItem(LANDING_LANGUAGE_STORAGE_KEY);
    if (isLandingLanguage(storedLanguage)) return storedLanguage;
  } catch {}

  return (navigator.language || "").toLowerCase().startsWith("es") ? "es" : "en";
}

function switchLandingLanguage(language: LandingLanguage): void {
  const nextLanguage: LandingLanguage = language === "es" ? "en" : "es";
  try {
    window.localStorage?.setItem(LANDING_LANGUAGE_STORAGE_KEY, nextLanguage);
  } catch {}

  const url = new URL(window.location.href);
  url.searchParams.set("lang", nextLanguage);
  window.location.href = url.href;
}

function setLandingMetaDescription(description: string): string | null {
  let meta = document.querySelector<HTMLMetaElement>('meta[name="description"]');
  if (!meta) {
    meta = document.createElement("meta");
    meta.name = "description";
    document.head.appendChild(meta);
  }
  const previousDescription = meta.getAttribute("content");
  meta.setAttribute("content", description);
  return previousDescription;
}

export function createOnlineDemoLanding(options: OnlineDemoLandingOptions = {}): OnlineDemoLanding {
  const app = document.getElementById("app");
  if (!app) throw new Error("Missing #app root");

  activeLanding?.dispose();

  const language = resolveLandingLanguage();
  const copy = LANDING_COPY[language];
  const discordHref = escapeHtml(normalizeSafeHref(options.discordInviteUrl, DEFAULT_STUDIO_DISCORD_INVITE_URL));
  const previousDocumentLanguage = document.documentElement.lang;
  const previousDocumentTitle = document.title;
  const previousMetaDescription = setLandingMetaDescription(copy.metaDescription);

  app.replaceChildren();
  document.documentElement.lang = copy.htmlLang;
  document.title = copy.pageTitle;
  document.documentElement.classList.add("online-demo-page");
  document.body.classList.add("online-demo-page");

  const style = document.createElement("style");
  style.setAttribute("data-online-demo-landing-style", "true");
  style.textContent = landingCss;
  document.head.appendChild(style);

  const root = document.createElement("main");
  root.id = "online-demo-landing";
  root.innerHTML = `
    <nav class="online-demo-nav" aria-label="${escapeHtml(copy.navStudio)}">
      <a class="online-demo-mark" href="#top" aria-label="PivotArena Game Studio">PivotArena Game Studio</a>
      <div class="online-demo-navlinks">
        <a class="online-demo-link" href="#estudio">${escapeHtml(copy.navStudio)}</a>
        <a class="online-demo-link" href="#juegos">${escapeHtml(copy.navGames)}</a>
        <a class="online-demo-link discord" href="${discordHref}" target="_blank" rel="noopener noreferrer">Discord</a>
        <button class="online-demo-link online-demo-language-toggle" type="button" data-language-toggle aria-label="${escapeHtml(copy.languageToggleLabel)}">${copy.languageToggleText}</button>
      </div>
    </nav>

    <section id="top" class="online-demo-hero" aria-labelledby="studio-title">
      <div class="online-demo-hero-copy">
        <span class="online-demo-label">${escapeHtml(copy.heroLabel)}</span>
        <h1 id="studio-title" class="online-demo-title">PIVOTARENA</h1>
        <p class="online-demo-copy">${escapeHtml(copy.heroCopy)}</p>
        <div class="online-demo-actions">
          <a class="online-demo-button primary" href="#juegos">${escapeHtml(copy.ctaGames)}</a>
          <a class="online-demo-button discord" href="${discordHref}" target="_blank" rel="noopener noreferrer">${escapeHtml(copy.joinDiscord)}</a>
        </div>
      </div>
      <div class="online-demo-visual">
        <img class="studio-feature-logo" src="/landing/build-more-tanks.png" alt="Build More Tanks!">
      </div>
    </section>

    <section id="juegos" class="online-demo-section" aria-labelledby="games-title">
      <h2 id="games-title">${escapeHtml(copy.gamesTitle)}</h2>
      <div class="online-demo-features">
        <article class="online-demo-feature">
          <div class="online-demo-media-slot">
            <img class="studio-card-logo" src="/landing/build-more-tanks.png" alt="Build More Tanks!">
          </div>
          <div>
            <span class="online-demo-label">${escapeHtml(copy.projectStatus)}</span>
            <h3>Build More Tanks!</h3>
            <p>${escapeHtml(copy.projectDescription)}</p>
          </div>
        </article>
      </div>
    </section>

    <section id="estudio" class="online-demo-section online-demo-support" aria-labelledby="about-title">
      <div>
        <span class="online-demo-label">${escapeHtml(copy.studioLabel)}</span>
        <h2 id="about-title">PivotArena Game Studio</h2>
        <p>${escapeHtml(copy.studioDescription)}</p>
      </div>
      <div class="online-demo-support-card">
        <div class="online-demo-support-row">
          <span><strong>${escapeHtml(copy.locationTitle)}</strong><span>${escapeHtml(copy.locationValue)}</span></span>
          <span class="online-demo-button secondary" aria-label="Argentina">AR</span>
        </div>
        <div class="online-demo-support-row">
          <span><strong>${escapeHtml(copy.activityTitle)}</strong><span>${escapeHtml(copy.activityValue)}</span></span>
          <a class="online-demo-button primary" href="#juegos">${escapeHtml(copy.navGames)}</a>
        </div>
      </div>
    </section>

    <footer class="online-demo-footer">
      <div class="online-demo-footer-inner">
        <h2>PivotArena<br>Game Studio</h2>
        <div class="online-demo-actions">
          <a class="online-demo-link" href="#estudio">${escapeHtml(copy.footerStudio)}</a>
          <a class="online-demo-link" href="#juegos">${escapeHtml(copy.footerGames)}</a>
          <a class="online-demo-link discord" href="${discordHref}" target="_blank" rel="noopener noreferrer">Discord</a>
        </div>
      </div>
      <div class="online-demo-footer-bottom">
        <span class="online-demo-argentina-badge">
          ${escapeHtml(copy.argentinaBadge)}
          <span class="online-demo-argentina-flag" aria-hidden="true"></span>
        </span>
      </div>
    </footer>
  `;

  app.appendChild(root);
  root.querySelector<HTMLButtonElement>("[data-language-toggle]")?.addEventListener("click", () => {
    switchLandingLanguage(language);
  });

  let disposed = false;
  const landing: OnlineDemoLanding = {
    dispose: () => {
      if (disposed) return;
      disposed = true;
      document.documentElement.lang = previousDocumentLanguage;
      document.title = previousDocumentTitle;
      const meta = document.querySelector<HTMLMetaElement>('meta[name="description"]');
      if (meta && previousMetaDescription !== null) meta.setAttribute("content", previousMetaDescription);
      document.documentElement.classList.remove("online-demo-page");
      document.body.classList.remove("online-demo-page");
      root.remove();
      style.remove();
      if (activeLanding === landing) activeLanding = null;
    },
  };

  activeLanding = landing;
  return landing;
}

